import type { DateValidationError } from '@mui/x-date-pickers/models'
import TableQueryFooter from './TableQueryFooter'
import { Select } from '@/components/select/Select'
import { columnIcons } from './column-icons'
import { MultiSelect } from '@/components/ui/MultiSelect'
import Filter from '@/assets/icons/table-filter.svg?react'
import { useId, useRef, useState } from 'react'
import { ColumnKind, FilterMatch, FilterOperator, TableColumn } from '../schemas'
import { Button } from '@/components/button/Button'
import { XClose } from '@/assets/icons'
import { IconButton } from '@/components/icon-button/IconButton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import TableDateInput from './TableDateInput'
import { isDateComparison, parseFilterDate } from '../date-filter'
import clsx from 'clsx'

// Presentation supports both query contracts; callers validate filters at their endpoint boundary.
type FilterCondition = { column_id: string; operator: FilterOperator | 'not_contains'; value: string }

const labels: Record<FilterCondition['operator'], string> = {
  contains: 'Contains',
  not_contains: 'Does not contain',
  equals: 'Equals',
  gt: 'Greater than',
  gte: 'At least',
  lt: 'Less than',
  lte: 'At most',
  is_blank: 'Is blank',
  is_not_blank: 'Is not blank',
}
const dateLabels: Partial<Record<FilterCondition['operator'], string>> = {
  gt: 'After',
  gte: 'On or after',
  lt: 'Before',
  lte: 'On or before',
}
const inputClass = 'h-10 min-w-0 rounded border border-brand-neutral-300 bg-white pl-3 pr-8 py-1 text-sm text-black placeholder:text-brand-neutral-600'

export type FilterColumn = Pick<TableColumn, 'id' | 'name' | 'kind' | 'source_file'> & {
  filterOperators?: FilterCondition['operator'][]
  filterOptions?: string[]
}

function defaultOperator(column: FilterColumn | undefined) {
  return column?.filterOperators?.[0] ?? (column?.kind === ColumnKind.Date ? FilterOperator.Equals : FilterOperator.Contains)
}

export default function TableFilters({
  disabled = false,
  columns,
  filters,
  filterMatch = FilterMatch.All,
  onChange,
}: {
  disabled?: boolean
  columns: FilterColumn[]
  filters: FilterCondition[]
  filterMatch?: FilterMatch
  onChange: (filters: FilterCondition[], match: FilterMatch) => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(filters)
  const [match, setMatch] = useState(filterMatch)
  const [submitted, setSubmitted] = useState(false)
  const [dateErrors, setDateErrors] = useState<DateValidationError[]>([])
  const [touchedDates, setTouchedDates] = useState<boolean[]>([])
  const [calendarOpen, setCalendarOpen] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const errorId = useId()
  const valueFields = useRef<(HTMLInputElement | HTMLButtonElement | null)[]>([])
  const invalidValues = draft.map((filter) =>
    isDateComparison(columns.find((column) => column.id === filter.column_id)?.kind, filter.operator)
      ? !parseFilterDate(filter.value)
      : filter.operator !== FilterOperator.IsBlank && filter.operator !== FilterOperator.IsNotBlank && !filter.value.trim()
  )
  const hasDateInput = draft.some((filter) => isDateComparison(columns.find((column) => column.id === filter.column_id)?.kind, filter.operator))
  const update = (index: number, change: Partial<FilterCondition>) => {
    setDraft(draft.map((item, i) => (i === index ? { ...item, ...change } : item)))
    if (change.column_id !== undefined || change.operator !== undefined) {
      setTouchedDates((values) => values.map((value, i) => (i === index ? false : value)))
      setDateErrors((values) => values.map((value, i) => (i === index ? null : value)))
    }
  }
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setCalendarOpen(false)
          setSubmitted(false)
          setTouchedDates([])
          setDateErrors([])
          setDraft(filters.length || !columns.length ? filters : [{ column_id: columns[0].id, operator: defaultOperator(columns[0]), value: '' }])
          setMatch(filterMatch)
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <IconButton
          disabled={disabled}
          size="sm"
          variant="plain"
          color={filters.length ? 'primary' : 'black'}
          icon={
            <span className="relative">
              <Filter className="h-4 w-4" />
              {filters.length > 0 && <span className="absolute -right-[3.5px] -top-[3.5px] h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
          }
          className={`disabled:!text-true-gray-200 ${filters.length ? 'bg-brand-50' : ''}`}
          aria-label={filters.length ? `Filters · ${filters.length}` : 'Filters'}
          aria-pressed={filters.length > 0}
          tooltipText={filters.length ? `Filter · ${filters.length}` : 'Filter'}
        />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        width="auto"
        showArrow={false}
        className={clsx(hasDateInput ? 'w-[680px]' : 'w-[560px]', 'max-w-[calc(100vw-32px)] border border-brand-neutral-200 p-4 shadow-menu')}
        aria-label="Table filters"
        onEscapeKeyDown={(event) => {
          if (calendarOpen) event.preventDefault()
        }}
      >
        <form
          ref={formRef}
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            setSubmitted(true)
            const firstInvalid = invalidValues.indexOf(true)
            if (firstInvalid !== -1) {
              valueFields.current[firstInvalid]?.focus()
              return
            }
            onChange(
              draft.map((filter) =>
                isDateComparison(columns.find((column) => column.id === filter.column_id)?.kind, filter.operator)
                  ? { ...filter, value: parseFilterDate(filter.value)!.format('YYYY-MM-DD') }
                  : filter
              ),
              match
            )
            setOpen(false)
          }}
        >
          <span className="text-lg font-medium leading-6">Filters</span>
          <div className="-m-1 flex max-h-[50vh] flex-col gap-3 overflow-y-auto p-1">
            {!draft.length && <p className="text-sm text-brand-neutral-600">No filters applied.</p>}
            {draft.map((item, index) => {
              const column = columns.find((col) => col.id === item.column_id)
              const operators =
                column?.filterOperators ??
                (column?.kind === ColumnKind.Text
                  ? [FilterOperator.Contains, FilterOperator.Equals, FilterOperator.IsBlank, FilterOperator.IsNotBlank]
                  : Object.values(FilterOperator))
              const needsValue = item.operator !== FilterOperator.IsBlank && item.operator !== FilterOperator.IsNotBlank
              const dateComparison = isDateComparison(column?.kind, item.operator)
              const invalid = (submitted || (dateComparison && touchedDates[index])) && invalidValues[index]
              const fieldErrorId = `${errorId}-${index}`
              const KindIcon = column?.source_file ? columnIcons.file : columnIcons[column?.kind ?? ColumnKind.Text]
              return (
                <div
                  key={index}
                  className={clsx(
                    'grid',
                    hasDateInput
                      ? 'grid-cols-[56px_minmax(0,1fr)_114px_190px_20px]'
                      : 'grid-cols-[56px_minmax(0,174px)_minmax(0,114px)_minmax(0,114px)_minmax(0,1fr)]',
                    'items-start gap-3 max-sm:grid-cols-[56px_minmax(0,1fr)_28px]'
                  )}
                >
                  {index === 0 ? (
                    <span className="flex h-10 items-center pl-2 text-sm">Where</span>
                  ) : (
                    <Select
                      ariaLabel={index === 1 ? 'Match conditions' : `Match conditions ${index + 1}`}
                      triggerClassName="border-0 bg-transparent pl-1 pr-0 text-black [&>div>span]:font-normal disabled:[&>span]:hidden"
                      className="!w-28 [&_[role=option]>div>span]:font-normal [&_[role=option]]:text-black"
                      selectedItemId={match}
                      disabled={index > 1}
                      onSelect={(value) => setMatch(value as FilterMatch)}
                      items={[
                        { id: FilterMatch.All, label: 'And' },
                        { id: FilterMatch.Any, label: 'Or' },
                      ]}
                    />
                  )}
                  <div className="relative min-w-0">
                    <Select
                      ariaLabel={`Filter column ${index + 1}`}
                      className="[&_[role=option]>div>span]:font-normal [&_[role=option]]:text-black"
                      arrowIcon="ChevronDown"
                      triggerClassName="text-black [&>div>span]:font-normal"
                      leadingIcon={<KindIcon className="h-4 w-4 text-brand-neutral-600" />}
                      selectedItemId={item.column_id}
                      onSelect={(value) =>
                        update(index, {
                          column_id: value,
                          operator: defaultOperator(columns.find((col) => col.id === value)),
                          value: '',
                        })
                      }
                      items={columns.map((col) => {
                        const Icon = col.source_file ? columnIcons.file : columnIcons[col.kind]
                        return { id: col.id, label: col.name, leadingIcon: <Icon className="h-4 w-4 shrink-0 text-brand-neutral-600" /> }
                      })}
                    />
                  </div>
                  <Select
                    ariaLabel={`Filter operator ${index + 1}`}
                    className="[&_[role=option]>div>span]:font-normal [&_[role=option]]:text-black"
                    arrowIcon="ChevronDown"
                    triggerClassName="text-black [&>div>span]:font-normal max-sm:col-start-2"
                    selectedItemId={item.operator}
                    onSelect={(value) => update(index, { operator: value as FilterOperator | 'not_contains' })}
                    items={operators.map((operator) => ({
                      id: operator,
                      label: (column?.kind === ColumnKind.Date ? dateLabels[operator] : undefined) ?? labels[operator],
                    }))}
                  />
                  {needsValue ? (
                    <div className="min-w-0 max-sm:col-start-2">
                      {column?.filterOptions ? (
                        <MultiSelect
                          ref={(element) => {
                            valueFields.current[index] = element
                          }}
                          aria-invalid={invalid || undefined}
                          aria-describedby={invalid ? fieldErrorId : undefined}
                          compact
                          ariaLabel={`Filter value ${index + 1}`}
                          placeholder="Select status"
                          className={`h-10 min-w-0 border-brand-neutral-300 ${invalid ? '!border-red-600 focus:!ring-red-600 hover:!ring-red-600' : ''}`}
                          options={column.filterOptions.map((option) => ({ value: option, label: option }))}
                          selected={item.value ? item.value.split(',') : []}
                          onChange={(selected) => update(index, { value: column.filterOptions?.filter((option) => selected.includes(option)).join(',') ?? '' })}
                        />
                      ) : isDateComparison(column?.kind, item.operator) ? (
                        <TableDateInput
                          key={`${item.column_id}-${item.operator}`}
                          value={item.value}
                          label={`Filter value ${index + 1}`}
                          inputRef={(element) => {
                            valueFields.current[index] = element
                          }}
                          error={Boolean(invalid)}
                          errorId={fieldErrorId}
                          onValidationError={(error) => setDateErrors((values) => draft.map((_, i) => (i === index ? error : values[i] ?? null)))}
                          onBlur={() => setTouchedDates((values) => draft.map((_, i) => i === index || Boolean(values[i])))}
                          containerRef={formRef}
                          onOpenChange={setCalendarOpen}
                          onChange={(value) => update(index, { value })}
                        />
                      ) : (
                        <input
                          ref={(element) => {
                            valueFields.current[index] = element
                          }}
                          aria-invalid={invalid || undefined}
                          aria-describedby={invalid ? fieldErrorId : undefined}
                          aria-label={`Filter value ${index + 1}`}
                          type={column?.filterOperators && column.kind === ColumnKind.Number ? 'number' : 'text'}
                          step="any"
                          placeholder="Value"
                          className={`${inputClass} w-full pr-2 ${invalid ? '!border-red-600 focus:!ring-red-600' : ''}`}
                          value={item.value}
                          onChange={(event) => update(index, { value: event.target.value })}
                        />
                      )}
                      {invalid && (
                        <p id={fieldErrorId} role="alert" className="mt-1 text-xs text-red-600">
                          {dateComparison
                            ? dateErrors[index] === 'minDate' || dateErrors[index] === 'maxDate'
                              ? 'Year must be 1900–2099'
                              : 'Enter a valid, complete date'
                            : column?.filterOptions
                            ? 'Select at least one status.'
                            : 'Enter a value.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="max-sm:hidden" />
                  )}
                  <IconButton
                    wrapperClassName="mt-1.5 sm:!w-5 max-sm:col-start-3 max-sm:row-start-1"
                    className="sm:!w-5"
                    type="button"
                    size="sm"
                    variant="plain"
                    color="neutral"
                    icon={<XClose />}
                    aria-label={`Remove filter ${index + 1}`}
                    onClick={() => {
                      setDraft(draft.filter((_, i) => i !== index))
                      setTouchedDates((values) => values.filter((_, i) => i !== index))
                      setDateErrors((values) => values.filter((_, i) => i !== index))
                    }}
                  />
                </div>
              )
            })}
          </div>
          <div className="flex h-10 items-center">
            <Button
              type="button"
              size="sm"
              color="secondary"
              variant="plain"
              className="!font-medium !text-black"
              disabled={!columns.length}
              onClick={() => setDraft([...draft, { column_id: columns[0].id, operator: defaultOperator(columns[0]), value: '' }])}
            >
              + Add condition
            </Button>
          </div>
          <TableQueryFooter
            applyDisabled={invalidValues.some(Boolean)}
            onClear={() => {
              setDraft([])
              setMatch(FilterMatch.All)
              onChange([], FilterMatch.All)
              setOpen(false)
            }}
            onCancel={() => setOpen(false)}
          />
        </form>
      </PopoverContent>
    </Popover>
  )
}
